import _ from 'underscore';

import {BrowserSupport} from '@tableau/browser-support';
import {Recurrence} from '@tableau/self-service-schedules';

import {incrementRouterSpaceReactKey} from '../../app/actions/spaces/IncrementRouterSpaceReactKeyAction';
import {getAppStore} from '../../app/AppStore';
import {Messages} from '../../codegen/Localize';
import {L10n} from '../../ts/L10n/L10n';
import {IDatasourcePublishResult, ISyncPublishResult} from '../controllers/DatasourcePublishResultCtrl';
import {FeatureFlags} from '../FeatureFlags';
import {DateHelpers} from '../helpers/DateHelpers';
import {ResultHelper} from '../../httpClient/utils/ResultHelper';
import {IDataSourceBase, IDataSourceDetailInfo} from '../models/IDataSourceInfo';
import {IRemoteRefreshScheduleInfo} from '../models/IRemoteRefreshScheduleInfo';
import {Module} from '../Module';
import {RefreshOptionType} from '../../content/resources/Datasources';
import {RemoteRefreshAgents} from '../resources/RemoteRefreshAgents';
import {RemoteRefreshSchedules} from '../resources/RemoteRefreshSchedules';
import {RefreshModeTypes} from '../ServerApi/types/IRemoteRefreshProperties';
import {IResult} from '../../httpClient/serverApi/types/IResponse';
import {ChangeDatasourceRefreshModeAction} from '../services/ChangeDatasourceRefreshModeAction';
import {ChangeDatasourceRemoteAgentAction} from '../services/ChangeDatasourceRemoteAgentAction';
import {ChangeDatasourceRemoteAgentReservationAction} from '../services/ChangeDatasourceRemoteAgentReservationAction';
import {
  IRemoteRefreshAgentOption,
  IRemoteRefreshPropertiesEditModel,
} from './ChangeDatasourceRemoteRefreshPropertiesHelper';
import {DesktopApplicationLaunchService} from '../services/DesktopApplicationLaunchService';
import {SessionSiteSelectors} from '../../app/selectors/SessionInfoSelectors';
import {PublishDatasourceSuccessDialog,} from '../../app/content/PublishDataSourceSuccessDialog';
import {ModalInstance, ReactModalService} from '../../commonInfra/dialogs/ReactModalService';
import {DialogPositioningBehavior, DialogType} from '@tableau/widgets';
import React from 'react';
import {PostPublishStep} from './PostPublishStep';
import {getSessionManagementServiceInstance} from '../../sessionManagement/SessionManagementService';
import {CreateExtractTasksMode} from '~src/extract/actions/CreateExtractTaskAction';
import {CreateDatasourceExtractTaskActionService} from '~src/extract/actions/CreateDatasourceExtractTaskAction';

export class PublishDatasourceAction {

  //// A special agent for the option to add a new agent.
  // "New agent" option that launches a Sync Client protocol handler when selected
  private newAgentLaunchOption: IRemoteRefreshAgentOption;
  // "New agent" option that does not launch Sync Client, used for reservations
  private newAgentReservationOption: IRemoteRefreshAgentOption;

  public static $inject: any = [
    '$rootScope',
  ];

  constructor(
    private $rootScope: ng.IRootScopeService,
  ) {
    this.newAgentReservationOption = {
      display: Messages.remoteRefreshAgentSetupLocal(),
      // "name" is unique for remote agents. Use a unique special value for testing equality with this option
      name: '@@reserve-local-agent@@'
    };

    this.newAgentLaunchOption = {
      display: Messages.remoteRefreshAgentSetupLocal(),
      // "name" is unique for remote agents. Use a unique special value for testing equality with this option.
      name: '@@launch-local-agent@@',
      launchesSyncClient: true
    };
  }

  public getNewAgentReservationOption() {
    return this.newAgentReservationOption;
  }

  public handlePublishedDatasource(countOfSchedules: number, publishResult: IDatasourcePublishResult, datasource: IDataSourceDetailInfo) {
    if (publishResult && publishResult.preferredRefreshOption) {
      // Make our best attempt to fire this off, even when the window closes.
      const unloadListener = () =>
      RemoteRefreshAgents.completeWorkflowDuringWindowUnload(publishResult.remoteAgentWorkflowId);
      window.addEventListener('unload', unloadListener, false);

      let handlingResult: Promise<any>;

      if (countOfSchedules === 0) {
        if (publishResult.preferredRefreshOption.suggestion === RefreshOptionType.LiveConnection) {
          // If it is a live connection check if it was published with live query sync
            if (publishResult.publishedWithLQS){
              // reach here if url is: datasources/id/publishresult?publishedWithLQS=y
              handlingResult = this.displaySuccessDialog(datasource, PostPublishStep.OnlineLiveQuerySync);
              // $MARK bmoulin TFSID:581383:As a User I can chose which LQA agent from post-publish success dialog
            } else {
              handlingResult = this.displaySuccessDialog(datasource, PostPublishStep.Done);
            }
        } else if (publishResult.preferredRefreshOption.suggestion === RefreshOptionType.Server) {
          // If it is a server-refresh
          handlingResult = this.displaySuccessDialog(datasource, PostPublishStep.ServerRefresh)
            .then(() => ChangeDatasourceRefreshModeAction.execute(datasource, RefreshModeTypes.Server, null, true, false))
            .then(() => CreateDatasourceExtractTaskActionService.forDatasource(datasource, {
              scope: this.$rootScope,
              targetElement: null
            }, CreateExtractTasksMode.AllowScheduleOnServer))
            .then(() => getAppStore().dispatch(incrementRouterSpaceReactKey()));

        } else if (publishResult.preferredRefreshOption.suggestion === RefreshOptionType.OnlineSync) {
          let shouldScheduleOnlyOnClient = false;
          if (!FeatureFlags.bridgeServerTriggeredClientSideRefreshForFileBasedDatasourceEnabled && datasource.isFileBased) {
            shouldScheduleOnlyOnClient = true;
          }

          const createExtractTasksMode = shouldScheduleOnlyOnClient ? CreateExtractTasksMode.AllowScheduleOnlyOnClient : CreateExtractTasksMode.AllowScheduleOnServerAndClient;
          const refreshMode = shouldScheduleOnlyOnClient ? RefreshModeTypes.Remote : RefreshModeTypes.Server;

          handlingResult = this.displaySuccessDialog(datasource, PostPublishStep.ServerTriggeredClientRefresh)
            .then(() => ChangeDatasourceRefreshModeAction.execute(datasource, refreshMode, null, true, false))
            .then(() => CreateDatasourceExtractTaskActionService.forDatasource(datasource, {
              scope: this.$rootScope,
              targetElement: null
            }, createExtractTasksMode)
              .then(() => getAppStore().dispatch(incrementRouterSpaceReactKey())));
        }

        handlingResult.finally(() => {
          // Clear this from the scope so the dialog won't appear again if the user navigates back to this state
          if (publishResult) {
            publishResult.preferredRefreshOption = null;
          }
        });
      }

      handlingResult.finally(() => {
        window.removeEventListener('unload', unloadListener, false);
        RemoteRefreshAgents.completeWorkflow(publishResult.remoteAgentWorkflowId);
      });
    }
  }

  private displaySuccessDialog(datasource: IDataSourceDetailInfo, postPublishStep: PostPublishStep): Promise<any> {
    let modalInstance: ModalInstance;
    const dialogProps = {
      postPublishStep: postPublishStep,
      datasourceSuccessMessage: Messages.publishDatasourceSuccessDialogTextPublished({ DATASOURCE_NAME: L10n.elideIfLong(datasource.name) }),
      close: () => modalInstance && modalInstance.close()
    };

    modalInstance = ReactModalService.open({
      title:  Messages.publishDatasourceSuccessDialogTitleComplete(),
      type: DialogType.LightweightTitleBar,
      positioningBehavior: DialogPositioningBehavior.Centered,
      dialogContent: React.createElement(PublishDatasourceSuccessDialog, dialogProps),
      showCloseButton: true,
      dialogWidth: 500,
      dismissDialogOnCloseAction: true,
      disableAutoFocus: true,
    });

    return Promise.resolve(modalInstance.result).finally(modalInstance.dismiss);
  }

  private getDefaultAgentName(agents: IRemoteRefreshAgentOption[]): string {
    if (agents.length === 0) return '';

    const recentSyncThreshold = 10*60*1000; // ten minutes, in milliseconds
    const nowTimestamp = Date.now();

    const mostRecentAgent: IRemoteRefreshAgentOption = _.max(agents, (agent) => agent.schedulesSyncedAt ? DateHelpers.fromISOString(agent.schedulesSyncedAt).getTime() : 0) as IRemoteRefreshAgentOption;

    // If the most recent agent sync'd within the recency threshold, return it, otherwise return the first in the list.
    if (mostRecentAgent.schedulesSyncedAt && (nowTimestamp - DateHelpers.fromISOString(mostRecentAgent.schedulesSyncedAt).getTime() < recentSyncThreshold)) {
      return mostRecentAgent.name;
    } else {
      return agents[0].name;
    }
  }

  public createRemoteRefreshSelfServiceSchedule(
    datasource: IDataSourceBase,
    refreshType: string,
    recurrence: Recurrence,
    editModel: IRemoteRefreshPropertiesEditModel,
    publishResult: ISyncPublishResult,
    suppressSuccessToast: boolean = false
  ): ng.IPromise<IResult> {
    // Create the remote refresh schedule
    const scheduleInfo: IRemoteRefreshScheduleInfo = {
      id: '',
      contentType: 'remoteRefreshSchedule',
      definition: '',
      refreshType: refreshType,
      scheduleDetails: recurrence.recurrenceDetails,
      scheduleType: recurrence.recurrenceType
    };

    return this.createRefreshSchedule(datasource, scheduleInfo, editModel, publishResult, suppressSuccessToast);
  }

  private createRefreshSchedule(
    datasource: IDataSourceBase,
    scheduleInfo: IRemoteRefreshScheduleInfo,
    editModel: IRemoteRefreshPropertiesEditModel,
    publishResult: ISyncPublishResult,
    suppressSuccessToast: boolean
  ): ng.IPromise<IResult> {
    return RemoteRefreshSchedules
      .create(datasource.id, scheduleInfo)
      .then((result: IResult) => this.rejectResultOnError(result))
      // Then change the refresh mode to 'remote agent'
      .then(() => ChangeDatasourceRefreshModeAction.execute(datasource, RefreshModeTypes.Remote, editModel.remoteAgentName, true, false))
      .then((result: IResult) => this.rejectResultOnError(result))
      .then(() => {

        if (editModel.remoteAgentName === this.newAgentLaunchOption.name) {
          // If we're adding a new remote agent, create new sync client and assign to this datasource
          return DesktopApplicationLaunchService.launchSyncClientAndAssignToDatasource(
            getSessionManagementServiceInstance().sessionSiteUrlName(),
            datasource.urlName);
        } else if (editModel.remoteAgentName === this.newAgentReservationOption.name) {
          return  ChangeDatasourceRemoteAgentReservationAction
            .execute(datasource, publishResult.remoteAgentWorkflowId, suppressSuccessToast);
        } else {
          // Otherwise, switch datasource to the selected remote agent
          return ChangeDatasourceRemoteAgentAction.execute(datasource,
            editModel.remoteAgentName, RefreshModeTypes.Remote, suppressSuccessToast,
            publishResult.remoteAgentWorkflowId);
        }
      })
      .then((result: IResult) => this.rejectResultOnError(result));
  }

  private rejectResultOnError(result: IResult){
    return ResultHelper.hasErrors(result) ? Promise.reject(result) : Promise.resolve(result);
  }

  private canLaunchSync(publishResult: ISyncPublishResult) {
    return BrowserSupport.IsWindows()
      && publishResult.isSyncAvailable
      && !publishResult.isSyncRunning
      // Instead of launching Sync Client from the browser,
      // a workflow ID implies that Desktop already launched the Sync Client.
      && _.isEmpty(publishResult.remoteAgentWorkflowId);
  }

  public canReserveSyncWithWorkflow(publishResult: ISyncPublishResult) {
    return !_.isEmpty(publishResult.remoteAgentWorkflowId);
  }
}

Module.instance().service('PublishDatasourceAction', PublishDatasourceAction);
